import React, {useCallback, useRef, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {TextInput as RNTextInput, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import TextInput from '../../../components/themed/TextInput';
import {InputLabel} from '../../../components/ui-controls/form/InputLabel';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Modal from '../../../components/ui-controls/Modal';
import {AppRoute} from '../../../models/AppRoute';
import {AccountScreenAction, RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {useStores} from '../../../stores/use-stores';
import rewardsTheme from '../../../theme/rewardsTheme';
import {PasswordRequirementsIndicator} from '../components/PasswordRequirementsIndicator';
import TogglePasswordVisibleIconButton from '../components/TogglePasswordVisibleIconButton';
import {WeakPasswordBypass} from '../components/WeakPasswordBypass';
import {usePasswordInput} from '../hooks';
import {
  PASSWORDS_DO_NOT_MATCH,
  useConfirmPasswordMismatch,
} from '../hooks/useConfirmPasswordMismatch';
import {AccountPasswordChangeFormData, accountPasswordChangeSchema} from '../schemas/authSchemas';

import {buildPasswordChangePayload} from './helpers';

const AccountChangePasswordScreen = observer(
  ({navigation: _navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_CHANGE_PASSWORD>) => {
    const {userStore} = useStores();

    const [isSubmitting, setSubmitting] = useState(false);
    const [isPasswordUpdatedModalVisible, setIsPasswordUpdatedModalVisible] = useState(false);

    const passwordRef = useRef<RNTextInput>(null);
    const confirmPasswordRef = useRef<RNTextInput>(null);

    const form = useForm<AccountPasswordChangeFormData>({
      resolver: zodResolver(accountPasswordChangeSchema),
      defaultValues: {
        password: '',
        confirmPassword: '',
        passwordValidationBypass: false,
      },
      mode: 'all', // Show validation errors immediately
    });

    const {control, handleSubmit, reset: resetForm} = form;

    const passwordInput = usePasswordInput({
      form,
      confirmFieldName: 'confirmPassword',
      passwordRef,
      confirmPasswordRef,
    });

    const {reset: resetPasswordState} = passwordInput;

    // DIS-1862: the mismatch line shows on blur, not while typing.
    const confirmMismatch = useConfirmPasswordMismatch(
      passwordInput.passwordValue,
      form.watch('confirmPassword'),
      confirmPasswordRef,
    );

    const submitSuccessCallback = () => {
      setSubmitting(false);
      if (passwordRef.current) {
        // @ts-expect-error - Type mismatch needs investigation
        passwordRef.current.clear();
      }
      setIsPasswordUpdatedModalVisible(true);
    };

    const postSubmitSuccess = () => {
      setIsPasswordUpdatedModalVisible(false);
      userStore.afterUpdateUser();
    };

    const submitErrorCallback = (error?: {status: string; message: string}) => {
      setSubmitting(false);
      if (error) {
        passwordInput.handleWeakPasswordError(error);
      }
    };

    const onSubmit = async (values: AccountPasswordChangeFormData) => {
      const showSuccessToast = false; // Don't show toast for password change, use modal instead
      setSubmitting(true);

      const userValues = buildPasswordChangePayload(values, userStore);

      await userStore.updateUserInfo(
        AccountScreenAction.CHANGE_PASSWORD,
        userValues,
        submitSuccessCallback,
        submitErrorCallback,
        {
          showSuccessToast,
        },
      );

      setSubmitting(false);
    };

    // Clear form when screen comes into focus (e.g., after successful password change)
    useFocusEffect(
      useCallback(() => {
        resetForm();
        resetPasswordState();
      }, [resetForm, resetPasswordState]),
    );

    const handleSubmitPress = handleSubmit(async (data: AccountPasswordChangeFormData) => {
      await onSubmit(data);
    }, confirmMismatch.onInvalidSubmit);

    return (
      <ThemeProvider theme={rewardsTheme}>
        <KeyboardAwareScrollView
          style={{backgroundColor: 'white'}}
          keyboardShouldPersistTaps="always"
          enableOnAndroid
          extraScrollHeight={40}>
          <ContentView>
            <View style={{gap: 8}}>
              <Text style={{marginVertical: 16}}>
                Enter your new password below. For security, choose a strong password with a mix of
                letters, numbers, and symbols.
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  color: '#6B7280',
                  fontStyle: 'italic',
                  marginBottom: 8,
                }}>
                * indicates required field
              </Text>

              <Controller
                control={control}
                name="password"
                render={({field: {onChange, onBlur, value}, fieldState: {error, isTouched}}) => (
                  <>
                    <TextInput
                      autoCapitalize="none"
                      label={<InputLabel required>Password</InputLabel>}
                      onBlur={() => {
                        passwordInput.passwordFieldHandlers.onBlur();
                        onBlur();
                      }}
                      onFocus={passwordInput.passwordFieldHandlers.onFocus}
                      onChangeText={(text: string) => {
                        passwordInput.passwordFieldHandlers.onChangeText(text);
                        onChange(text);
                      }}
                      onSubmitEditing={passwordInput.passwordFieldHandlers.onSubmitEditing}
                      ref={passwordRef}
                      returnKeyType="next"
                      secureTextEntry={!passwordInput.isPasswordVisible}
                      value={value || ''}
                      touched={isTouched}
                      errorMessage={error?.type === 'manual' ? error.message : undefined}
                      errorMessageWhenTouched={error?.type === 'manual' ? error.message : undefined}
                      renderErrorMessage={!!error?.type && error.type === 'manual'}
                      rightIconContainerStyle={{
                        position: 'absolute',
                        right: 10,
                      }}
                      rightIcon={
                        <TogglePasswordVisibleIconButton {...passwordInput.toggleButtonProps} />
                      }
                    />
                    <PasswordRequirementsIndicator {...passwordInput.indicatorProps} />
                  </>
                )}
              />
              <WeakPasswordBypass {...passwordInput.bypassCheckboxProps} />
              <Controller
                control={control}
                name="confirmPassword"
                render={({field: {onChange, onBlur, value}, fieldState: {error}}) => {
                  // DIS-1862: the hook decides when the mismatch line shows; the
                  // schema's own mismatch error is never shown.
                  const confirmError = confirmMismatch.showMismatch
                    ? PASSWORDS_DO_NOT_MATCH
                    : confirmMismatch.mismatch
                      ? undefined
                      : error?.message;

                  return (
                    <TextInput
                      autoCapitalize="none"
                      label={<InputLabel required>Confirm password</InputLabel>}
                      onBlur={() => {
                        confirmMismatch.onConfirmBlur();
                        onBlur();
                      }}
                      onChangeText={(text: string) => {
                        passwordInput.confirmFieldHandlers.onChangeText(text);
                        onChange(text);
                      }}
                      ref={confirmPasswordRef}
                      secureTextEntry={!passwordInput.isConfirmPasswordVisible}
                      value={value || ''}
                      touched={form.formState.touchedFields.confirmPassword}
                      errorMessage={confirmError}
                      errorMessageWhenTouched={
                        form.formState.touchedFields.password ||
                        form.formState.touchedFields.confirmPassword
                          ? confirmError
                          : undefined
                      }
                      rightIconContainerStyle={{
                        position: 'absolute',
                        right: 10,
                      }}
                      rightIcon={
                        <TogglePasswordVisibleIconButton
                          {...passwordInput.confirmToggleButtonProps}
                        />
                      }
                    />
                  );
                }}
              />

              <View style={{alignItems: 'flex-end', marginTop: 16}}>
                <Button
                  buttonStyle={{width: 194}}
                  title="Save"
                  onPress={handleSubmitPress}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              </View>
            </View>
          </ContentView>
          <Modal isVisible={isPasswordUpdatedModalVisible} title="Password updated successfully">
            <Button
              title="Continue"
              onPress={postSubmitSuccess}
              buttonStyle={{width: 150, marginTop: 16}}
            />
          </Modal>
        </KeyboardAwareScrollView>
      </ThemeProvider>
    );
  },
);

export default AccountChangePasswordScreen;
